import React, { useState } from 'react';
import { AppBar, Toolbar, Typography, IconButton, Drawer, List, ListItem, ListItemText, Box, Badge } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { Link, useLocation } from 'react-router-dom';
import { useContext } from 'react';
import { ShopContext } from '../context/shop-context';

const Navbar = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const { cartItems } = useContext(ShopContext);

  // Calculate total items in the cart
  const totalCartItems = Object.values(cartItems).reduce(
    (sum, item) => sum + item.quantity,
    0
  );

  const toggleDrawer = (open) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setDrawerOpen(open);
  };

  const navLinks = [
    { text: 'Home', path: '/' },
    { text: 'Shop', path: '/shop' },
    { text: 'About', path: '/about' },
    {  path: '/cart', icon: true },
    { text: 'Contact Us', path: '/contact' },
    { text: 'Return Policy', path: '/return-policy' },
  ];

  return (
    <AppBar position="sticky" sx={{ backgroundColor: '#1c0a49', minHeight: '120px' }}>
      <Toolbar
        sx={{
          minHeight: '120px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: isSmallScreen ? 'space-between' : 'center',
          gap: isSmallScreen ? 0 : 2,
        }}
      >
        {isSmallScreen ? (
          <>
            {/* Logo on the far left */}
            <Box sx={{ flex: 1 }}>
              <img src="whimssical-stitches.png" alt="Whimsical Stitches" style={{ height: '100px' }} />
            </Box>
            {/* Menu Icon with Cart Badge */}
            <IconButton
              edge="end"
              color="inherit"
              aria-label="menu"
              onClick={toggleDrawer(true)}
              sx={{ color: '#d75efb' }}
            >
              <Badge badgeContent={totalCartItems} color="error">
                <MenuIcon />
              </Badge>
            </IconButton>
          </>
        ) : (
          <>
            {/* Navbar Links (centered) */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                position: 'absolute',
                left: '40%',
                transform: 'translateX(-50%)',
              }}
            >
              {navLinks.slice(0, 3).map(({ text, path }) => (
                <Typography key={text} variant="h6">
                  <Link
                    to={path}
                    style={{
                      textDecoration: 'none',
                      color: location.pathname === path ? '#d75efb' : 'white',
                      fontWeight: location.pathname === path ? 'bold' : 'normal',
                      transition: 'color 0.3s ease',
                    }}
                    onMouseOver={(e) => (e.target.style.color = '#ffb3ff')}
                    onMouseOut={(e) => (e.target.style.color = location.pathname === path ? '#d75efb' : 'white')}
                  >
                    {text}
                  </Link>
                </Typography>
              ))}
            </Box>

            {/* Centered Logo */}
            <Box sx={{ display: 'flex', justifyContent: 'center', flex: 1 }}>
              <img src="whimssical-stitches.png" alt="Whimsical Stitches" style={{ height: '100px' }} />
            </Box>

            {/* Navbar Links with Cart */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, position: 'absolute', right: '26%' }}>
              {navLinks.slice(3).map(({ text, path, icon }) => (
                <Typography key={text} variant="h6">
                  <Link
                    to={path}
                    style={{
                      textDecoration: 'none',
                      color: location.pathname === path ? '#d75efb' : 'white',
                      fontWeight: location.pathname === path ? 'bold' : 'normal',
                      transition: 'color 0.3s ease',
                    }}
                    onMouseOver={(e) => (e.target.style.color = '#ffb3ff')}
                    onMouseOut={(e) => (e.target.style.color = location.pathname === path ? '#d75efb' : 'white')}
                  >
                    {icon ? (
                      <Badge badgeContent={totalCartItems} color="error">
                        <ShoppingCartIcon />
                      </Badge>
                    ) : (
                      text
                    )}
                  </Link>
                </Typography>
              ))}
            </Box>
          </>
        )}
      </Toolbar>

      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        sx={{
          '& .MuiDrawer-paper': {
            backgroundColor: '#10075a',
            color: 'white',
          },
        }}
      >
        <Box
          sx={{ width: 250 }}
          role="presentation"
          onClick={toggleDrawer(false)}
          onKeyDown={toggleDrawer(false)}
        >
          <IconButton onClick={toggleDrawer(false)} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
          <List>
            {navLinks.map(({ text, path, icon }) => (
              <ListItem
                key={text}
                component={Link}
                to={path}
                sx={{
                  color: location.pathname === path ? '#d75efb' : 'white',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {icon && (
                  <Badge badgeContent={totalCartItems} color="error" sx={{ mr: 1 }}>
                    <ShoppingCartIcon />
                  </Badge>
                )}
                <ListItemText primary={text} />
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>
    </AppBar>
  );
};

export default Navbar;
